import React from "react";
import styled, { keyframes, css } from "styled-components";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import { COLORS, responsive } from "Styles/Constants";

/* ------------------------------------------------------------------ *
 * VARIANT PALETTE — built from the site theme (Midnight Navy shell,   *
 * Crimson Red accent) so the in-game banner reads as part of the      *
 * product, not as a browser alert.                                    *
 * ------------------------------------------------------------------ */
const VARIANT_THEME = {
	success: { accent: "#7EDD03", accentDeep: "#4FA300", text: "#A7EF52", glow: "126, 221, 3" },
	error: { accent: COLORS.AccentRed, accentDeep: "#B81B1D", text: "#FF8082", glow: "238, 45, 47" },
	warning: { accent: COLORS.GOLDEN, accentDeep: "#D98A00", text: "#FFD166", glow: "255, 180, 14" },
	info: { accent: COLORS.SKY_BLUE, accentDeep: "#0069AE", text: "#6DCBFF", glow: "0, 168, 255" },
};

const variantStyles = Object.keys(VARIANT_THEME).map((variant) => {
	const { accent, accentDeep, text, glow } = VARIANT_THEME[variant];

	return css`
		&.toast-${variant} {
			.toast-card {
				border-color: rgba(${glow}, 0.45);
				box-shadow: 0 18px 45px rgba(2, 12, 28, 0.55), 0 0 0 1px rgba(${glow}, 0.18), 0 0 32px rgba(${glow}, 0.22);
			}

			.accent-rail {
				background: linear-gradient(180deg, ${accent} 0%, ${accentDeep} 100%);
				box-shadow: 0 0 18px rgba(${glow}, 0.65);
			}

			.icon-badge {
				background: linear-gradient(145deg, ${accent} 0%, ${accentDeep} 100%);
				box-shadow: 0 6px 18px rgba(${glow}, 0.4);

				&::after {
					border-color: rgba(${glow}, 0.55);
				}
			}

			.toast-title {
				color: ${text};
			}

			.glow-wash {
				background: radial-gradient(120% 140% at 0% 0%, rgba(${glow}, 0.22) 0%, rgba(${glow}, 0) 62%);
			}

			.progress-bar {
				background: linear-gradient(90deg, ${accentDeep} 0%, ${accent} 100%);
				box-shadow: 0 0 12px rgba(${glow}, 0.7);
			}
		}
	`;
});

/* ---------------------------------- *
 * ANIMATIONS                         *
 * ---------------------------------- */
const popIn = keyframes`
	0%   { opacity: 0; transform: translateY(-18px) scale(0.92); }
	55%  { opacity: 1; transform: translateY(3px)   scale(1.02); }
	78%  { transform: translateY(-1px) scale(0.995); }
	100% { opacity: 1; transform: translateY(0)     scale(1); }
`;

const ringPulse = keyframes`
	0%   { opacity: 0;    transform: scale(1); }
	20%  { opacity: 0.85; transform: scale(1.08); }
	100% { opacity: 0;    transform: scale(1.5); }
`;

const iconPop = keyframes`
	0%   { transform: scale(0.4) rotate(-12deg); opacity: 0; }
	60%  { transform: scale(1.15) rotate(4deg);  opacity: 1; }
	100% { transform: scale(1) rotate(0deg);     opacity: 1; }
`;

const drain = keyframes`
	from { transform: scaleX(1); }
	to   { transform: scaleX(0); }
`;

/* ---------------------------------- *
 * BANNER                             *
 * ---------------------------------- */
export const SnackbarContentWrapper = styled(({ css: _css, ...rest }) => <SnackbarContent {...rest} />)`
	&.event-banner {
		background: transparent;
		box-shadow: none;
		padding: 0;
		margin-top: 40px;
		min-width: 0;
		border-radius: 0;
		flex-wrap: nowrap;

		${responsive.PHABLET`
			margin-top: 16px;
			width: calc(100vw - 24px);
		`}

		.notification-message {
			padding: 0;
			width: 100%;
		}

		.toast-card {
			position: relative;
			display: flex;
			align-items: center;
			gap: 16px;
			overflow: hidden;
			box-sizing: border-box;
			padding: 16px 14px 16px 26px;
			min-width: 340px;
			max-width: 560px;
			border-radius: 18px;
			border: 1px solid transparent;
			background: linear-gradient(135deg, #0b3055 0%, ${COLORS.PrimaryBlue} 58%, #010f22 100%);
			animation: ${popIn} 420ms cubic-bezier(0.24, 1.2, 0.42, 1) both;

			${responsive.PHABLET`
				min-width: 0;
				width: 100%;
				gap: 12px;
				padding: 13px 8px 13px 20px;
				border-radius: 14px;
			`}
		}

		.glow-wash {
			position: absolute;
			inset: 0;
			pointer-events: none;
		}

		.accent-rail {
			position: absolute;
			top: 0;
			left: 0;
			bottom: 0;
			width: 6px;
			border-radius: 18px 0 0 18px;
		}

		.icon-badge {
			position: relative;
			flex: 0 0 auto;
			display: flex;
			align-items: center;
			justify-content: center;
			height: 46px;
			width: 46px;
			border-radius: 50%;

			${responsive.PHABLET`
				height: 38px;
				width: 38px;
			`}

			.badge-icon {
				position: relative;
				z-index: 1;
				font-size: 28px;
				color: #ffffff;
				filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
				animation: ${iconPop} 480ms cubic-bezier(0.24, 1.2, 0.42, 1) 90ms both;

				${responsive.PHABLET`
					font-size: 22px;
				`}
			}

			&::after {
				content: "";
				position: absolute;
				inset: -3px;
				border-radius: 50%;
				border: 2px solid transparent;
				opacity: 0;
				pointer-events: none;
				animation: ${ringPulse} 1.4s ease-out infinite;
			}
		}

		.text-block {
			flex: 1 1 auto;
			min-width: 0;
			display: flex;
			flex-direction: column;
			gap: 3px;

			.toast-title {
				margin: 0;
				font-family: "Outfit", sans-serif;
				font-size: 13px;
				font-weight: 700;
				line-height: 1.1;
				letter-spacing: 2.2px;
				text-transform: uppercase;

				${responsive.PHABLET`
					font-size: 11px;
					letter-spacing: 1.6px;
				`}
			}

			.toast-message {
				margin: 0;
				font-family: "Outfit", sans-serif;
				font-size: 17px;
				font-weight: 400;
				line-height: 1.32;
				color: #e9f1fa;
				white-space: pre-line;
				overflow-wrap: anywhere;

				${responsive.PHABLET`
					font-size: 14px;
				`}
			}
		}

		.close-btn {
			flex: 0 0 auto;
			align-self: center;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 28px;
			height: 28px;
			padding: 0;
			margin: 0;
			color: rgba(255, 255, 255, 0.5);
			transition: color 160ms ease, background-color 160ms ease;

			.MuiIconButton-label {
				display: flex;
				align-items: center;
				justify-content: center;
				line-height: 0;
			}

			&:hover {
				color: #ffffff;
				background-color: rgba(255, 255, 255, 0.1);
			}

			.close-icon {
				font-size: 18px;
				display: block;
			}
		}

		.progress-bar {
			display: block;
			position: absolute;
			left: 0;
			bottom: 0;
			height: 3px;
			width: 100%;
			transform-origin: left center;
			animation-name: ${drain};
			animation-timing-function: linear;
			animation-fill-mode: forwards;
			pointer-events: none;
		}

		/* Respect users who have asked the OS for less motion. */
		@media (prefers-reduced-motion: reduce) {
			.toast-card,
			.badge-icon,
			.icon-badge::after,
			.progress-bar {
				animation: none !important;
			}
		}
	}

	${variantStyles}
`;
